import React, { useEffect, useRef, useState } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  Image,
  ActivityIndicator,
  FlatList,
} from "react-native";
import IChatModel, {
  IChatConversationModel,
  IChatConversationTypeModel,
} from "../services/models/chat";
import ChatHeaderComponent from "./ChatHeaderComponent";
import { ClipIcon, ImageIcon, SendIcon } from "./Icons";
import { colors } from "./Shared";
import { View, Text } from "./Themed";
import ChatService, { chatErrors } from "../services/chats";
import * as ImagePicker from "expo-image-picker";

import { RootState } from "../services/store/app";
import { useSelector } from "react-redux";
import { defaultUser } from "../services/models/user";
import { getDocumentAsync } from "expo-document-picker";
import MessageItem from "./chat/MessageItem";
import copy from "fast-copy";
import PreViewBeforeSend from "./chat/PreViewBeforeSend";
import StorageService, { storageErrors } from "../services/storage";
import ResourceSelector from "./settings/resource/Selector";
import { Resource } from "../services/Resource";
import { AntDesign } from "@expo/vector-icons";
import ModalCandidatePicker from "./ModalCandidatePicker";
import { CandidateProps } from "../types/candidate";

const chatService = new ChatService();
const storageService = new StorageService();

interface Props {
  chat: IChatModel;
  onBack: () => void;
}

export default function ChatComponent({ chat, onBack }: Props) {
  const common = useSelector((state: RootState) => state.commonReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(defaultUser);
  const [conversation, setConversation] = useState<IChatConversationModel[]>([]);
  const [messageType, setMessageType] = useState<IChatConversationTypeModel>("text");
  const [message, setMessage] = useState("");
  const [uri, setUri] = useState("");
  const [localUri, setLocalUri] = useState("");
  const [fileName, setFileName] = useState("");
  const [especialFuncView, setEspecialFuncView] = useState(false);

  const [showResourcesSelector, setShowResourcesSelector] = useState(false);
  const [showCandidatePicker, setShowCandidatePicker] = useState(false);
  const [candidate, setCandidate] = useState<CandidateProps | null>(null);

  useEffect(() => {
    if (!candidate) {
      return;
    }

    setIsLoading(true);
    setLocalUri(candidate.resumeUrl);
    setFileName(candidate.resumeDocName!);
    setMessageType("file");
    setUri(candidate.resumeUrl);
    setShowCandidatePicker(false);
    setIsLoading(false);
  }, [candidate]);

  useEffect(() => {
    const conversationToSet = copy(chat.conversation).reverse();
    setConversation(conversationToSet);
  }, [chat]);

  useEffect(() => {
    setUser(common.user);
  }, [common]);

  useEffect(() => {
    const readLastMessage = async () => {
      if (!chat.lastMessage.read && chat.lastMessage.uid !== user.uid) {
        await chatService.readLastMessage(chat.id || "", chat.lastMessage);
      }
    };

    return () => {
      readLastMessage();
    };
  }, [conversation, user]);

  const isMe = (uid: string) => user.uid === uid;
  const scrollRef: any = useRef<ScrollView>();

  const send = async () => {
    if (isLoading) {
      return;
    }

    const conversationCopy = copy(conversation);
    const messageToSend = {
      message,
      fileName,
      createAt: new Date().toJSON(),
      type: messageType,
      ownerId: common.user.uid,
      url: localUri,
    };

    // console.log(messageToSend);

    conversationCopy.push(messageToSend);
    messageToSend.url = uri;
    setConversation(conversationCopy);
    reset();

    const sendMessage = await chatService.sendMessage(chat.id, messageToSend);
    if (sendMessage === chatErrors.systemError) {
      return console.log("error");
    }
    chatService.sendPushNotification(chat.ownersId[0], chat.target, message, {
      action: "openChat",
      id: chat.id,
    });
    if (!chat.ownersId.includes(common.user.uid)) {
      chatService.takeChat(chat.id, {
        uid: common.user.uid,
        picture: common.user.picture,
        name: common.user.name,
      });
    }
    
    if (candidate) {
      setMessage(`${candidate.name}\n${candidate.profession}\n${candidate.location}`);
      setCandidate(null);
    }
  };

  const reset = () => {
    setMessage("");
    setUri("");
    setLocalUri("");
    setMessageType("text");
    setFileName("");
  };

  const resetPreView = () => {
    setUri("");
    setLocalUri("");
    setMessageType("text");
    setFileName("");
    setMessage("");
  };

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      base64: true,
      allowsEditing: false,
      quality: 0,
    });

    if (!result.cancelled) {
      const { uri } = result;
      setLocalUri(uri);
      setMessageType("image");
      const ext = uri.split(";")[0].split(":")[1];
      setIsLoading(true);
      const picture = await chatService.uploadStringImage(uri, ext);
      setUri(picture);
      setIsLoading(false);
      await Image.prefetch(picture);
    }
  };

  // const scrollToBottom = () =>
  //   scrollRef?.current?.scrollToEnd({ animated: true });

  const dropOut = async () => {
    await chatService.dropOut(common.user.uid, chat);
  };

  const updateChatImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: false,
      base64: false,
      quality: 0,
    });

    if (!result.cancelled) {
      const { uri } = result;
      const ext = uri.split(";")[0].split(":")[1];

      const picture = await chatService.uploadStringImage(uri, ext);
      (async () => {
        await Image.prefetch(picture);
      })();
      await chatService.updatePicture(chat.id, picture);
    }
  };

  const onSelectDocument = async () => {
    const result = await getDocumentAsync({});
    if (result.type === "cancel") {
      return;
    }

    const { name, uri } = result;
    setIsLoading(true);
    setLocalUri(uri);
    setFileName(name);
    setMessageType("file");
    const ext = name.split(".").reverse()[0];

    const upload = await storageService.uploadFile(uri, ext);
    if (
      upload === storageErrors.emptyFields ||
      upload === storageErrors.systemError
    ) {
      setIsLoading(false);
      resetPreView();
      alert("Something went wrong, please try it again");
    }
    setUri(upload);
    setIsLoading(false);
    // console.log(name, uri);
  };

  const deletePicture = () => {
    if (chat.picture) {
      chatService.updatePicture(chat.id, "", true);
    }
  };

  const finishIt = async () => {
    const finish = await chatService.finishChat(common.user.uid, chat, 2);
    if (finish === "OK") {
      onBack();
    }
  };

  const onSetResource = ({ fileName, name, uri }: Resource) => {
    console.log(fileName, name, uri);
    
    setFileName(fileName);
    setUri(uri);
    setLocalUri(uri);
    setMessageType("file");
    setMessage(name);
  }

  return (
    <View style={{ flex: 1, flexDirection: "column", backgroundColor: '#fff' }}>
      <ChatHeaderComponent
        title={`${chat.target} (${
          chat?.ownersData[0]?.name ? chat?.ownersData[0]?.name : ""
        })`}
        onBack={onBack}
        onPressDots={() => setEspecialFuncView(!especialFuncView)}
      />

      <FlatList
        style={contentStyles.container}
        contentContainerStyle={{ padding: 20 }}
        ref={scrollRef}
        data={conversation}
        renderItem={({ item, index }) => (
          <MessageItem
            conversation={item}
            key={index}
            isMe={isMe(item.ownerId)}
          />
        )}
      />

      <View style={footerStyles.container}>
        <PreViewBeforeSend
          uri={localUri}
          type={messageType}
          fileName={fileName}
          onPressClose={resetPreView}
        />
        <View
          style={{
            backgroundColor: "transparent",
            flexDirection: "row",
            height: 60,
            paddingVertical: 10,
          }}
        >
          <Pressable style={footerStyles.icon} onPress={onSelectDocument}>
            <ClipIcon />
          </Pressable>

          <Pressable style={footerStyles.icon} onPress={pickImage}>
            <ImageIcon />
          </Pressable>

          <Pressable style={footerStyles.icon} onPress={() => setShowCandidatePicker(true)}>
						<AntDesign name="filetext1" size={24} color="black" />
          </Pressable>

          <TextInput
            value={message}
            onChangeText={setMessage}
            onSubmitEditing={send}
            style={footerStyles.input}
            placeholder="Type here..."
          />

          {isLoading ? (
            <ActivityIndicator
              color={colors.bg.primary}
              style={{ paddingHorizontal: 5 }}
            />
          ) : (
            <Pressable style={footerStyles.icon} onPress={send}>
              <SendIcon />
            </Pressable>
          )}
        </View>
      </View>

      {especialFuncView && (
        <EspecialFuntionsComponent
          closeChat={onBack}
          dropOut={dropOut}
          changePicture={updateChatImage}
          deletePicture={deletePicture}
          onOpenResources={() => {
            setEspecialFuncView(false);
            setShowResourcesSelector(true)
          }}
          finish={finishIt}
        />
      )}

      <ResourceSelector
        isVisible={showResourcesSelector}
        onClose={() => setShowResourcesSelector(false)}
        onSelect={onSetResource}
      />

      <ModalCandidatePicker
        visible={showCandidatePicker}
        close={() => setShowCandidatePicker(false)}
        onSelect={(candidate: CandidateProps) => setCandidate(candidate)}
      />
    </View>
  );
}

const contentStyles = StyleSheet.create({
  container: { flex: 1, padding: 5, transform: [{ scaleY: -1 }] },
  messageContainer: {
    marginTop: 5,
  },
  meContainer: { flexDirection: "row-reverse" },
  himContainer: { flexDirection: "row" },
});

const footerStyles = StyleSheet.create({
  container: {
    backgroundColor: colors.bg.light,
    // height: 60,
    flexDirection: "column",
  },
  input: {
    marginHorizontal: 2,
    backgroundColor: colors.bg.medium,
    borderRadius: 40,
    flex: 1,
    paddingHorizontal: 15,
  },
  icon: {
    height: "100%",
    paddingHorizontal: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
});

const EspecialFuntionsComponent = ({
  closeChat,
  dropOut,
  changePicture,
  finish,
  deletePicture,
  onOpenResources,
}: {
  closeChat: () => void;
  dropOut: () => void;
  changePicture: () => void;
  deletePicture: () => void;
  onOpenResources: () => void;
  finish: () => void;
}) => {
  return (
    <View
      style={{
        backgroundColor: colors.bg.light,
        position: "absolute",
        top: 55,
        minWidth: 70,
        right: 10,
        paddingVertical: 5,
        paddingHorizontal: 10,
        zIndex: 1,
        flexDirection: "column",
        borderRadius: 5,
        shadowColor: "#00000065",
        shadowOffset: { width: 0, height: 0 },
        shadowRadius: 10,
        shadowOpacity: 1,
      }}
    >
      <Pressable
        onPress={closeChat}
        style={{ marginHorizontal: "auto", paddingVertical: 8 }}
      >
        <Text>Close</Text>
      </Pressable>

      <Pressable
        onPress={changePicture}
        style={{ marginHorizontal: "auto", paddingVertical: 8 }}
      >
        <Text>Chat Picture</Text>
      </Pressable>

      <Pressable
        onPress={onOpenResources}
        style={{ marginHorizontal: "auto", paddingVertical: 8 }}
      >
        <Text>Resources</Text>
      </Pressable>

      <Pressable
        onPress={deletePicture}
        style={{ marginHorizontal: "auto", paddingVertical: 8 }}
      >
        <Text>Delete Picture</Text>
      </Pressable>

      <Pressable
        onPress={dropOut}
        style={{ marginHorizontal: "auto", paddingVertical: 8 }}
      >
        <Text>Drop out</Text>
      </Pressable>

      <Pressable
        onPress={finish}
        style={{ marginHorizontal: "auto", paddingVertical: 8 }}
      >
        <Text>Finish</Text>
      </Pressable>
    </View>
  );
};
